(() => {
  const previewKeys = ['preview_theme_id', '_ab', '_fd', '_sc', 'pb', 'locale'];
  const preservedUrl = (href) => {
    const url = new URL(href, window.location.href);
    const current = new URL(window.location.href);
    if (url.origin === current.origin) previewKeys.forEach(key => {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    });
    if (url.origin === current.origin && !url.searchParams.has('preview_theme_id') && window.Shopify?.theme?.role === 'unpublished' && window.Shopify.theme.id) url.searchParams.set('preview_theme_id', String(window.Shopify.theme.id));
    return url;
  };
  async function fetchWithTimeout(url, options) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try { return await fetch(url, { ...options, signal:controller.signal }); }
    finally { window.clearTimeout(timeout); }
  }
  const stringsFor = (root, selector) => JSON.parse(root.querySelector(selector).textContent);
  const updateLinks = root => root.querySelectorAll('[data-mv-preserve-preview]').forEach(link => { link.href = preservedUrl(link.getAttribute('href')).href; });

  function initializeAddresses(root) {
    const config = root.querySelector('[data-address-strings]');
    if (!config || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const strings = stringsFor(root, '[data-address-strings]');
    const grid = root.querySelector('[data-address-grid]');
    const pager = root.querySelector('[data-address-pagination]');
    const more = root.querySelector('[data-address-more]');
    const status = root.querySelector('[data-address-status]');
    const loadError = root.querySelector('[data-address-load-error]');
    const modal = root.querySelector('#address_modal');
    let activeForm = null;
    let trigger = null;
    let dialogKeyHandler = null;
    let previousScrollLock = false;
    let busy = false;
    let retry = null;
    let deleting = false;

    const setStatus = message => { status.textContent = message || ''; status.hidden = !message; };
    const setBusy = value => {
      busy = value;
      grid.setAttribute('aria-busy', String(value));
      more.setAttribute('aria-disabled', String(value));
      root.querySelector('[data-address-retry]').disabled = value;
      setStatus(value ? strings.loading : '');
    };
    const updateCount = () => {
      const shown = grid.querySelectorAll(':scope > [data-address-id]').length;
      root.querySelector('[data-address-count]').textContent = strings.showing.replace('__SHOWN__', shown).replace('__TOTAL__', pager.dataset.total);
    };
    const returnUrl = () => preservedUrl(root.dataset.returnUrl).href;

    function populateProvinces(form, initial) {
      const country = form.elements['address[country]'];
      const province = form.elements['address[province]'];
      const field = province.closest('[data-province-field]');
      let provinces = [];
      try { provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch {}
      province.replaceChildren(new Option(strings.chooseProvince, ''));
      provinces.forEach(([value, label]) => province.add(new Option(label, value)));
      field.hidden = provinces.length === 0;
      province.disabled = provinces.length === 0;
      if (initial && province.dataset.default) {
        const selected = Array.from(province.options).find(option => option.value === province.dataset.default || option.text === province.dataset.default);
        if (selected) province.value = selected.value;
      }
    }

    function closed() {
      if (dialogKeyHandler) document.removeEventListener('keydown', dialogKeyHandler, true);
      dialogKeyHandler = null;
      activeForm = null;
      const returnFocus = trigger;
      queueMicrotask(() => {
        if (previousScrollLock) document.body.classList.add('overflow-hidden');
        if (returnFocus?.isConnected) returnFocus.focus();
      });
    }

    async function openAddress(id = 'new', opener = document.activeElement) {
      if (deleting || document.getElementById('dac-address-dialog')) return;
      await customElements.whenDefined('modal-component');
      if (document.getElementById('dac-address-dialog')) return;
      id = String(id || 'new');
      const template = root.querySelector(`#template_address_form_${CSS.escape(id)}`);
      if (!template) return;
      trigger = opener;
      previousScrollLock = document.body.classList.contains('overflow-hidden');
      modal.setConfig({
        id:'dac-address-dialog', title:id === 'new' ? strings.add : strings.edit,
        templateId:template.id, successText:id === 'new' ? strings.add : strings.save,
        cancelText:strings.cancel, onSuccess:submitAddress, onClose:closed
      });
      modal.open(opener);
      const dialog = document.getElementById('dac-address-dialog');
      const section = dialog.querySelector('section');
      const heading = dialog.querySelector('[data-modal-title], #modal-title');
      heading.id = 'dac-address-dialog-title';
      const dialogRoot = dialog instanceof HTMLDialogElement ? dialog : section;
      dialogRoot.setAttribute('role', 'dialog');
      dialogRoot.setAttribute('aria-modal', 'true');
      dialogRoot.setAttribute('aria-labelledby', heading.id);
      section.tabIndex = -1;
      const close = dialog.querySelector('header button.modal-close-trigger');
      if (close) {
        close.setAttribute('aria-label', strings.close);
        window.DACCloseControl?.apply(close, strings.close);
      }
      activeForm = dialog.querySelector('form');
      const submit = dialog.querySelector('[data-modal-success], #modal-success-text');
      submit.type = 'submit';
      submit.setAttribute('form', activeForm.id);
      activeForm.elements.return_to.value = returnUrl();
      const country = activeForm.elements['address[country]'];
      const placeholder = country.querySelector('option[value="---"]');
      if (placeholder) { placeholder.value = ''; placeholder.textContent = strings.chooseCountry; }
      if (Array.from(country.options).some(option => option.value === country.dataset.default)) country.value = country.dataset.default;
      else country.value = '';
      populateProvinces(activeForm, true);
      country.addEventListener('change', () => populateProvinces(activeForm, false));
      activeForm.addEventListener('submit', event => { event.preventDefault(); submitAddress(); });
      dialogKeyHandler = event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); modal.close(); return; }
        if (event.key !== 'Tab') return;
        const controls = Array.from(section.querySelectorAll('button,a[href],input,select,textarea,[tabindex="0"]')).filter(el => !el.disabled && el.type !== 'hidden' && !el.closest('[hidden]'));
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); section.focus(); }
        else if (event.shiftKey && (document.activeElement === first || !section.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !section.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', dialogKeyHandler, true);
      activeForm.querySelector('input:not([type="hidden"])')?.focus();
    }

    function submitAddress(event) {
      event?.preventDefault();
      if (!activeForm || activeForm.dataset.submitting || !activeForm.reportValidity()) return;
      const form = activeForm;
      const submit = document.querySelector('#dac-address-dialog [data-modal-success], #dac-address-dialog #modal-success-text');
      const originalLabel = submit.textContent;
      form.dataset.submitting = 'true';
      submit.disabled = true;
      submit.textContent = strings.saving;
      form.elements.return_to.value = returnUrl();
      try { HTMLFormElement.prototype.submit.call(form); }
      catch {
        delete form.dataset.submitting;
        submit.disabled = false;
        submit.textContent = originalLabel;
        const error = form.querySelector('[data-address-error]');
        error.textContent = strings.formError;
        error.hidden = false;
      }
    }

    function deleteAddress(id, opener) {
      if (deleting || !window.confirm(strings.deleteConfirm)) return;
      const form = root.querySelector(`#delete_form_${CSS.escape(String(id))}`);
      if (!form) return;
      const iframe = document.createElement('iframe');
      iframe.name = 'dac_address_delete_target';
      iframe.hidden = true;
      iframe.title = '';
      iframe.src = 'about:blank';
      let submitted = false;
      let timeout;
      const failed = () => {
        window.clearTimeout(timeout);
        iframe.remove();
        deleting = false;
        opener.disabled = false;
        setStatus(strings.deleteError);
      };
      deleting = true;
      opener.disabled = true;
      setStatus(strings.deleting);
      iframe.addEventListener('load', () => {
        if (!submitted) {
          submitted = true;
          form.elements._method.value = 'delete';
          form.elements.return_to.value = returnUrl();
          form.target = iframe.name;
          try { HTMLFormElement.prototype.submit.call(form); } catch { failed(); }
          return;
        }
        try {
          if (iframe.contentDocument?.querySelector('.errors, [data-address-error]:not([hidden])')) { failed(); return; }
        } catch {}
        window.clearTimeout(timeout);
        iframe.remove();
        window.location.assign(returnUrl());
      });
      timeout = window.setTimeout(failed, 30000);
      document.body.appendChild(iframe);
    }

    function pageUrl(page, section = true) {
      const url = preservedUrl(window.location.href);
      url.searchParams.set('page', String(page));
      if (section) url.searchParams.set('section_id', root.dataset.sectionId);
      else url.searchParams.delete('section_id');
      return url;
    }
    async function readPage(page) {
      const response = await fetchWithTimeout(pageUrl(page), { credentials:'same-origin' });
      if (!response.ok) throw new Error('Address page unavailable');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const result = doc.querySelector('[data-personal-details]');
      if (!result?.querySelector('[data-address-pagination]')) throw new Error('Address list unavailable');
      return result;
    }
    function appendCards(source, prepend = false) {
      const cards = Array.from(source.querySelectorAll('[data-address-grid] > [data-address-id]')).filter(card => !grid.querySelector(`[data-address-id="${CSS.escape(card.dataset.addressId)}"]`));
      if (prepend) grid.prepend(...cards); else grid.append(...cards);
      updateCount();
      document.dispatchEvent(new Event('dac:addresses-grid-updated'));
    }
    async function restorePages() {
      if (busy) return;
      const current = Number(pager.dataset.currentPage);
      if (!Number.isInteger(current) || current <= 1 || current > Number(pager.dataset.pages)) return;
      setBusy(true); loadError.hidden = true;
      try {
        for (let page = current - 1; page >= 1; page--) appendCards(await readPage(page), true);
        retry = null;
      } catch { retry = restorePages; loadError.hidden = false; }
      finally { setBusy(false); }
    }
    async function loadMore() {
      if (busy || more.hidden) return;
      setBusy(true); loadError.hidden = true;
      try {
        for (let step = 0; step < 2; step++) {
          const page = Number(pager.dataset.currentPage) + 1;
          if (page > Number(pager.dataset.pages)) break;
          const result = await readPage(page);
          const nextPager = result.querySelector('[data-address-pagination]');
          if (Number(nextPager.dataset.currentPage) !== page) throw new Error('Unexpected address page');
          pager.dataset.total = nextPager.dataset.total;
          appendCards(result);
          pager.dataset.currentPage = nextPager.dataset.currentPage;
          pager.dataset.pages = nextPager.dataset.pages;
          more.href = pageUrl(page + 1, false).href;
          more.hidden = page >= Number(pager.dataset.pages);
          window.history.replaceState(window.history.state, '', pageUrl(page, false));
          if (more.hidden) break;
        }
        retry = null;
      } catch { retry = loadMore; loadError.hidden = false; }
      finally { setBusy(false); }
    }
    root.addEventListener('click', event => {
      const edit = event.target.closest('[data-address-edit]');
      if (edit) { openAddress(edit.dataset.addressEdit, edit); return; }
      const remove = event.target.closest('[data-address-delete]');
      if (remove) { deleteAddress(remove.dataset.addressDelete, remove); return; }
      if (event.target.closest('[data-address-more]')) { event.preventDefault(); loadMore(); }
      if (event.target.closest('[data-address-retry]') && retry) retry();
    });
    window.openEditAddress = id => openAddress(id);
    window.editAddress = submitAddress;
    window.deleteAddress = id => deleteAddress(id, root.querySelector(`[data-address-delete="${CSS.escape(String(id))}"]`));
    updateCount();
    restorePages();
  }

  function initializeEmail(root) {
    if (!root.querySelector('[data-email-form]') || root.dataset.ready) return;
    root.dataset.ready = 'true';
    const strings = stringsFor(root, '[data-email-strings]');
    const form = root.querySelector('[data-email-form]');
    const input = root.querySelector('#new-email');
    const button = root.querySelector('#new-email-btn');
    const success = root.querySelector('#success-message');
    const error = root.querySelector('[data-email-error]');
    const retry = root.querySelector('[data-email-retry]');
    let token = new URL(window.location.href).searchParams.get('confirm-email');
    let busy = false;
    const showSuccess = message => { error.hidden = true; success.querySelector('.success-message-content').textContent = message; success.hidden = false; };
    const showError = message => { success.hidden = true; error.querySelector('#error-message').textContent = message; error.hidden = false; error.focus(); };
    async function post(path, payload) {
      const response = await fetchWithTimeout(path, { method:'POST', credentials:'same-origin', headers:{ 'ngrok-skip-browser-warning':'true' }, body:JSON.stringify(payload) });
      let data;
      try { data = await response.json(); } catch { throw new Error('Invalid response'); }
      if (!response.ok || data.success !== true) return { success:false, message:typeof data.message === 'string' ? data.message : '' };
      return { success:true, message:typeof data.message === 'string' ? data.message : '' };
    }
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (busy) return;
      input.value = input.value.trim();
      if (!form.reportValidity()) return;
      if (input.value.toLowerCase() === root.dataset.currentEmail.trim().toLowerCase()) { showError(strings.same); input.focus(); return; }
      busy = true; button.disabled = true; button.textContent = strings.sending; error.hidden = true; success.hidden = true;
      form.setAttribute('aria-busy', 'true');
      try {
        const result = await post('/apps/account-email/request-change', { newEmail:input.value });
        if (!result.success) throw new Error(result.message || strings.error);
        showSuccess(result.message || strings.sent);
        input.disabled = true;
        form.hidden = true;
        success.focus();
      } catch (failure) {
        showError(failure.message === 'Invalid response' || failure.name === 'AbortError' || failure instanceof TypeError ? strings.error : failure.message || strings.error);
        button.disabled = false;
      } finally { busy = false; button.textContent = strings.send; form.setAttribute('aria-busy', 'false'); }
    });
    async function confirmEmail() {
      if (busy || !token) return;
      busy = true; form.hidden = true; retry.hidden = true; retry.disabled = true;
      showSuccess(strings.confirming);
      try {
        const result = await post('/apps/account-email/confirm-change', { token });
        if (!result.success) throw new Error(result.message || strings.confirmError);
        showSuccess(result.message || strings.confirmed);
        const clean = new URL(window.location.href);
        clean.searchParams.delete('confirm-email');
        window.history.replaceState(window.history.state, '', clean);
        token = null;
        success.focus();
      } catch (failure) {
        showError(failure.message === 'Invalid response' || failure.name === 'AbortError' || failure instanceof TypeError ? strings.confirmError : failure.message || strings.confirmError);
        retry.hidden = false;
      } finally { busy = false; retry.disabled = false; }
    }
    retry.addEventListener('click', confirmEmail);
    if (token) confirmEmail();
  }
  const initialize = () => {
    document.querySelectorAll('[data-personal-details],[data-email-details]').forEach(root => {
      updateLinks(root);
      if (root.matches('[data-personal-details]')) initializeAddresses(root);
      else initializeEmail(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once:true });
  else initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
